'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import type {
  BlogArticleData,
  BlogArticleItem,
  BlogArticleProps,
  BlogArticleTrackingParams,
  BlogArticleValidExposureParams,
} from './types';

const DEFAULT_SECTION_BACKGROUND = '#f2f3f5';
const DEFAULT_HEADING_COLOR = '#1a1c1e';
const DEFAULT_HEADING_SUBTITLE_COLOR = '#626668';
const DEFAULT_TITLE_COLOR = '#282828';
const DEFAULT_SECONDARY_COLOR = '#6c6c6c';

function hasVisibleContent(item: BlogArticleItem) {
  return Boolean(item.jump_image || item.title || item.des || item.btn_text);
}

function getPlainText(value?: string) {
  return value?.replace(/<[^>]*>/g, '').trim() || '';
}

function useTrackingParams(
  item: BlogArticleItem,
  index: number,
  indexData: BlogArticleData,
  moduleIndex: number,
): BlogArticleTrackingParams {
  return useMemo(() => {
    const jumpData = item.jump_info || item;

    return {
      module_index: moduleIndex,
      module_name: indexData.component_id,
      module_id: String(indexData.blocks_advances_id || ''),
      module_title: indexData.layout_title || '',
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: String(jumpData.jump_type || ''),
      mkt_jump_content: String(jumpData.Jump_target || ''),
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(jumpData.sku?.shop_sku || ''),
      business_sku: String(jumpData.sku?.business_sku || ''),
      product_sku: String(jumpData.sku?.sku || ''),
      product_id: String(jumpData.sku?.id || ''),
      product_name: String(jumpData.sku?.short_name || ''),
      group_id: String(jumpData.product_group?.id || ''),
      group_name: String(jumpData.product_group?.name || ''),
    };
  }, [index, indexData, item, moduleIndex]);
}

function buildValidExposureParams(
  item: BlogArticleItem,
  index: number,
  indexData: BlogArticleData,
  moduleIndex: number,
): BlogArticleValidExposureParams {
  const jumpData = item.jump_info || item;

  return {
    module_index: moduleIndex,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: indexData.layout_title || '',
    module_name: indexData.component_id,
    module_tab_name: '',
    mkt_index: index,
    mkt_index_sub: '',
    mkt_jump_type: String(jumpData.jump_type ?? ''),
    mkt_jump_content: String(jumpData.Jump_target ?? ''),
  };
}

function useTrackingHandlers({
  trackingParams,
  validExposureParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<BlogArticleProps, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: BlogArticleTrackingParams;
  validExposureParams: BlogArticleValidExposureParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC blog article exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(validExposureParams);
    } catch (error) {
      logger.error('2026 PC blog article click tracking error:', error);
    }
  }, [onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(validExposureParams),
  });

  return {
    cardRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

interface BlogArticleCardProps
  extends Pick<BlogArticleProps, 'moduleIndex' | 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> {
  index: number;
  indexData: BlogArticleData;
  item: BlogArticleItem;
}

function BlogArticleCard({
  item,
  index,
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BlogArticleCardProps) {
  const trackingParams = useTrackingParams(item, index, indexData, moduleIndex);
  const validExposureParams = buildValidExposureParams(item, index, indexData, moduleIndex);
  const { cardRef, handleClick } = useTrackingHandlers({
    trackingParams,
    validExposureParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const jumpTarget = useMemo(() => {
    const jumpData = item.jump_info || item;

    return {
      jump_type: jumpData.jump_type,
      Jump_target: jumpData.Jump_target ?? undefined,
      sku: jumpData.sku?.shop_sku ? { shop_sku: jumpData.sku.shop_sku } : undefined,
      product_group: jumpData.product_group,
    };
  }, [item]);

  const titleText = getPlainText(item.title);
  const accessibleLabel = titleText || getPlainText(item.des) || `Blog article ${index + 1}`;

  return (
    <li ref={cardRef} className="min-w-0">
      <MagicJump
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={accessibleLabel}
        className="group block h-full rounded-[12px]"
      >
        <article className="flex h-full min-h-[363px] flex-col rounded-[12px] bg-white p-[24px]">
          <div className="flex h-[200px] w-full shrink-0 items-center justify-center" aria-hidden>
            {item.jump_image ? (
              <Image
                src={item.jump_image}
                alt={titleText}
                width={240}
                height={200}
                loading={moduleIndex < 3 ? 'eager' : 'lazy'}
                priority={moduleIndex < 3}
                className="block h-full w-full max-w-[240px] object-contain"
              />
            ) : null}
          </div>

          <div className="mt-[24px] flex min-h-[99px] flex-1 flex-col items-start">
            {item.title ? (
              <h4
                className="w-full font-bb text-[20px] leading-[24px]"
                style={{ color: item.title_color || DEFAULT_TITLE_COLOR }}
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
            ) : null}

            {item.des ? (
              <p
                className="mt-[12px] line-clamp-2 w-full font-br text-[14px] leading-[17px]"
                style={{ color: item.des_color || DEFAULT_SECONDARY_COLOR }}
                dangerouslySetInnerHTML={{ __html: item.des }}
              />
            ) : null}

            {item.btn_text ? (
              <span
                className="mt-auto inline-flex items-center pt-[12px] font-br text-[14px] leading-[17px]"
                style={{ color: item.btn_word_color || DEFAULT_SECONDARY_COLOR }}
              >
                {item.btn_text}
                <span className="ml-[4px]" aria-hidden>
                  &gt;
                </span>
              </span>
            ) : null}
          </div>
        </article>
      </MagicJump>
    </li>
  );
}

export default function BlogArticle({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BlogArticleProps) {
  const articles = (indexData.jump_target || []).filter(hasVisibleContent);
  const showHeading = indexData.layout_title_status === 1 && Boolean(indexData.layout_title);

  if (articles.length === 0) return null;

  return (
    <section
      className={`w-full py-[60px] ${showHeading ? 'min-h-[562px]' : ''}`}
      style={{ backgroundColor: indexData.bg_color || DEFAULT_SECTION_BACKGROUND }}
    >
      {showHeading ? (
        <header className="flex flex-col items-center gap-[8px] px-[24px] text-center">
          <h3
            className="w-full font-bb text-[32px] leading-[39px]"
            style={{ color: indexData.layout_title_color || DEFAULT_HEADING_COLOR }}
            dangerouslySetInnerHTML={{ __html: indexData.layout_title || '' }}
          />
          {indexData.layout_subtitle ? (
            <h4
              className="w-full font-br text-[20px] leading-[24px]"
              style={{
                color:
                  indexData.layout_subtitle_color?.trim() ||
                  indexData.layout_subtitl_color?.trim() ||
                  DEFAULT_HEADING_SUBTITLE_COLOR,
              }}
              dangerouslySetInnerHTML={{ __html: indexData.layout_subtitle }}
            />
          ) : null}
        </header>
      ) : null}

      <ul className={`grid grid-cols-4 gap-[12px] px-[48px] ${showHeading ? 'mt-[28px]' : ''}`}>
        {articles.map((item, index) => (
          <BlogArticleCard
            key={item.id || `${item.title || 'blog-article'}-${index}`}
            item={item}
            index={index}
            indexData={indexData}
            moduleIndex={moduleIndex}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </ul>
    </section>
  );
}
